import React from 'react';
import { Routes, Route, useNavigate, Link } from 'react-router-dom';
import ImagePickerField from './ImagePickerField';

const SectionCard = ({ title, icon, path, description }) => (
  <Link 
    to={path}
    className="group bg-white p-8 rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl hover:border-black transition-all duration-300"
  >
    <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300 inline-block">{icon}</div>
    <h3 className="text-xl font-playfair font-black text-black mb-2 uppercase tracking-wide">{title}</h3>
    <p className="text-black text-sm font-medium">{description}</p>
    <div className="mt-6 flex items-center text-[10px] font-black uppercase tracking-widest text-black transition-colors">
      Uredi sekciju 
      <svg className="w-3 h-3 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M9 5l7 7-7 7" />
      </svg>
    </div>
  </Link>
);

const SectionList = () => (
  <div className="space-y-8 animate-fade-in">
    <div className="flex flex-col gap-2">
      <h1 className="text-3xl font-playfair font-black text-black uppercase">Postavke Sekcija</h1>
      <p className="text-black font-medium italic">Odaberite sekciju koju želite modifikovati</p>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <SectionCard 
        title="Hero" 
        icon="🌅" 
        path="hero" 
        description="Glavni naslov, podnaslov i jačina zatamnjenja pozadine."
      />
      <SectionCard 
        title="Portfolio" 
        icon="🖼️" 
        path="portfolio" 
        description="Slike i naslovi za kategorije usluga (Vjenčanja, Filmovi, itd.)"
      />
      <SectionCard 
        title="Story" 
        icon="📖" 
        path="story" 
        description="O meni sekcija sa slikom i tri pasusa teksta."
      />
      <SectionCard 
        title="Galerija" 
        icon="🖼️" 
        path="gallery" 
        description="Mreža fotografija (Grid) sa glavnim naslovom sekcije."
      />
      <SectionCard 
        title="Films" 
        icon="🎬" 
        path="films" 
        description="Lista filmova i kratkih reelova koje želite prikazati na posebnoj stranici."
      />
      <SectionCard 
        title="Uskoro" 
        icon="✨" 
        path="#" 
        description="Mogućnost dodavanja novih sekcija u budućnosti."
      />
    </div>
  </div>
);

const HeroSettings = ({ settings, handleChange, loading, handleSettingsSubmit, navigate, setSettings }) => (
  <div className="animate-fade-in space-y-6">
    <div className="flex items-center justify-between mb-8 overflow-x-auto">
      <button 
        onClick={() => navigate('/admin/sections')}
        className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-black hover:text-black transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M15 19l-7-7 7-7" />
        </svg>
        Nazad na listu
      </button>
      <h2 className="text-2xl font-black text-black uppercase tracking-tight whitespace-nowrap">Uređivanje: Hero</h2>
    </div>

    <form onSubmit={handleSettingsSubmit} className="space-y-12">
      <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Title BS */}
          <div className="space-y-2">
            <label className="block text-[10px] font-black text-black uppercase tracking-widest">Naslov (Bosanski)</label>
            <input
              type="text"
              name="heroTitleBs"
              value={settings.heroTitleBs || ''}
              onChange={handleChange}
              placeholder="Npr. Timeless stories"
              className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-black transition-all text-sm font-medium"
            />
          </div>

          {/* Title EN */}
          <div className="space-y-2">
            <label className="block text-[10px] font-black text-black uppercase tracking-widest">Naslov (Engleski)</label>
            <input
              type="text"
              name="heroTitleEn"
              value={settings.heroTitleEn || ''}
              onChange={handleChange}
              placeholder="E.g. Timeless stories"
              className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-black transition-all text-sm font-medium"
            />
          </div>

          {/* Subtitle BS */}
          <div className="space-y-2">
            <label className="block text-[10px] font-black text-black uppercase tracking-widest">Podnaslov (Bosanski)</label>
            <input
              type="text"
              name="heroSubtitleBs"
              value={settings.heroSubtitleBs || ''}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-black transition-all text-sm font-medium"
            />
          </div>

          {/* Subtitle EN */}
          <div className="space-y-2">
            <label className="block text-[10px] font-black text-black uppercase tracking-widest">Podnaslov (Engleski)</label>
            <input
              type="text"
              name="heroSubtitleEn"
              value={settings.heroSubtitleEn || ''}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-black transition-all text-sm font-medium"
            />
          </div>

          {/* Motto BS */}
          <div className="space-y-2">
            <label className="block text-[10px] font-black text-black uppercase tracking-widest">Motto (Bosanski)</label>
            <input
              type="text"
              name="heroMottoBs"
              value={settings.heroMottoBs || ''}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-black transition-all text-sm font-medium"
            />
          </div>

          {/* Motto EN */}
          <div className="space-y-2">
            <label className="block text-[10px] font-black text-black uppercase tracking-widest">Motto (Engleski)</label>
            <input
              type="text"
              name="heroMottoEn"
              value={settings.heroMottoEn || ''}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-black transition-all text-sm font-medium"
            />
          </div>

          {/* Hero Images Picker */}
          <div className="md:col-span-2 space-y-6 pt-6 border-t border-gray-50">
            <ImagePickerField 
              label="Hero Slike (Slider)"
              description="Odaberite do 3 slike koje će se izmjenjivati na vrhu naslovne strane."
              multiple={true}
              value={settings.heroImages || []}
              onSelect={(images) => setSettings(prev => ({ ...prev, heroImages: images }))}
            />
          </div>

          {/* Overlay Brightness */}
          <div className="md:col-span-2 space-y-4">
             <div className="flex justify-between items-center bg-gray-50 p-6 rounded-3xl">
                <div className="flex flex-col">
                  <label className="text-[10px] font-black text-black uppercase tracking-widest mb-1">Jačina zatamnjenja pozadine</label>
                  <p className="text-[10px] text-black font-medium">Pomjerite klizač za kontrolu prozirnosti crnog sloja preko slike (0% - bez zatamnjenja, 100% - potpuno crno).</p>
                </div>
                <div className="text-xl font-black text-black">{Math.round((settings.heroOverlayOpacity || 0.1) * 100)}%</div>
             </div>
             <input
                type="range"
                name="heroOverlayOpacity"
                min="0"
                max="1"
                step="0.01"
                value={settings.heroOverlayOpacity || 0.1}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  setSettings(prev => ({ ...prev, heroOverlayOpacity: val }));
                }}
                className="w-full h-1.5 bg-gray-100 rounded-full appearance-none cursor-pointer accent-black"
              />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={loading}
          className="px-10 py-4 bg-black text-white rounded-2xl font-black uppercase tracking-[0.2em] text-[10px] hover:bg-gray-800 transition-all shadow-xl shadow-black/20 disabled:opacity-50"
        >
          {loading ? 'Spašavanje...' : 'Sačuvaj izmjene'}
        </button>
      </div>
    </form>
  </div>
);

const GallerySettings = ({ settings, handleChange, loading, handleSettingsSubmit, navigate, setSettings }) => {
  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex items-center justify-between mb-8 overflow-x-auto">
        <button 
          onClick={() => navigate('/admin/sections')}
          className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-black hover:text-black transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M15 19l-7-7 7-7" />
          </svg>
          Nazad na listu
        </button>
        <h2 className="text-2xl font-black text-black uppercase tracking-tight whitespace-nowrap">Uređivanje: Galerija</h2>
      </div>

      <form onSubmit={handleSettingsSubmit} className="space-y-12">
        <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm space-y-10">
          
          {/* Main Title Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pb-10 border-b border-gray-50">
            <div className="space-y-2">
              <label className="block text-[10px] font-black text-black uppercase tracking-widest">Naslov Sekcije (Bosanski)</label>
              <input
                type="text"
                name="galleryTitleBs"
                value={settings.galleryTitleBs || ''}
                onChange={handleChange}
                placeholder="Npr. Svaki detalj je dio uspomene koju stvaramo zajedno."
                className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-black transition-all text-sm font-medium"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-[10px] font-black text-black uppercase tracking-widest">Naslov Sekcije (Engleski)</label>
              <input
                type="text"
                name="galleryTitleEn"
                value={settings.galleryTitleEn || ''}
                onChange={handleChange}
                placeholder="E.g. Every detail is part of the memory we create together."
                className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-black transition-all text-sm font-medium"
              />
            </div>
          </div>

          {/* Gallery Images Selection */}
          <div className="space-y-8">
            <div className="flex flex-col gap-1">
              <h3 className="text-sm font-black text-black uppercase tracking-[0.2em]">Slike u Galeriji</h3>
              <p className="text-black text-[10px] font-medium italic">Ovdje odaberite slike koje želite prikazati u galeriji na sajtu.</p>
            </div>
            
            <div className="p-6 bg-gray-50/50 rounded-3xl border border-gray-100 space-y-4 hover:bg-white transition-all group">
              <ImagePickerField 
                label="Odabrane slike"
                description="Odaberite sve slike koje želite da se pojavljuju u galeriji."
                multiple={true}
                value={settings.galleryImages || []}
                onSelect={(images) => setSettings(prev => ({ ...prev, galleryImages: images }))}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end p-4">
          <button
            type="submit"
            disabled={loading}
            className="px-12 py-5 bg-black text-white rounded-[2rem] font-black uppercase tracking-[0.2em] text-[11px] hover:bg-gray-800 transition-all shadow-2xl shadow-black/20"
          >
            {loading ? 'Spašavanje...' : 'Sačuvaj izmjene'}
          </button>
        </div>
      </form>
    </div>
  );
};

const StorySettings = ({ settings, handleChange, loading, handleSettingsSubmit, navigate, setSettings }) => (
  <div className="animate-fade-in space-y-6">
    <div className="flex items-center justify-between mb-8 overflow-x-auto">
      <button 
        onClick={() => navigate('/admin/sections')}
        className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-black hover:text-black transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M15 19l-7-7 7-7" />
        </svg>
        Nazad na listu
      </button>
      <h2 className="text-2xl font-black text-black uppercase tracking-tight whitespace-nowrap">Uređivanje: Story (O meni)</h2>
    </div>

    <form onSubmit={handleSettingsSubmit} className="space-y-12">
      <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm space-y-10">
        
        {/* Paragraph 1 */}
        <div className="space-y-6 pb-10 border-b border-gray-50">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-black text-black uppercase tracking-[0.2em]">Prvi Pasus (Glavna poruka)</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-2">
              <label className="block text-[10px] font-black text-black uppercase tracking-widest">Bosanski</label>
              <textarea
                name="storyPara1Bs"
                value={settings.storyPara1Bs || ''}
                onChange={handleChange}
                placeholder="Vaša ljubavna priča je jedinstvena i zaslužuje da bude ispričana na najljepši, najemotivniji i najvječniji način."
                rows="3"
                className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-black transition-all text-sm font-serif italic leading-relaxed"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-[10px] font-black text-black uppercase tracking-widest">Engleski</label>
              <textarea
                name="storyPara1En"
                value={settings.storyPara1En || ''}
                onChange={handleChange}
                placeholder="Your love story is unique, and it deserves to be told in the most beautiful, emotional, and timeless way."
                rows="3"
                className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-black transition-all text-sm font-serif italic leading-relaxed"
              />
            </div>
          </div>
        </div>

        {/* Paragraph 2 & 3 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 pb-10 border-b border-gray-50">
          <div className="space-y-6">
            <h3 className="text-sm font-black text-black uppercase tracking-[0.2em]">Drugi Pasus</h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="block text-[10px] font-black text-black uppercase tracking-widest ml-1">Bosanski</label>
                <textarea
                  name="storyPara2Bs"
                  value={settings.storyPara2Bs || ''}
                  onChange={handleChange}
                  placeholder="Kroz objektiv svoje kamere, nastojim uhvatiti ne samo slike i video zapise, već samu suštinu vašeg dana — smijeh, suze, radost i tihe trenutke između njih."
                  rows="4"
                  className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-black transition-all text-sm font-medium"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-[10px] font-black text-black uppercase tracking-widest ml-1">Engleski</label>
                <textarea
                  name="storyPara2En"
                  value={settings.storyPara2En || ''}
                  onChange={handleChange}
                  placeholder="Through the lens of my camera, I strive to capture not just images and videos, but the very essence of your day — the laughter, the tears, the joy, and the quiet moments in between."
                  rows="4"
                  className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-black transition-all text-sm font-medium"
                />
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <h3 className="text-sm font-black text-black uppercase tracking-[0.2em]">Treći Pasus (Italic)</h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="block text-[10px] font-black text-black uppercase tracking-widest ml-1">Bosanski</label>
                <textarea
                  name="storyPara3Bs"
                  value={settings.storyPara3Bs || ''}
                  onChange={handleChange}
                  placeholder="Svaki par ima priču vrijednu slavlja, a ja sam tu da vašu pretvorim u kinematografsko remek-djelo."
                  rows="4"
                  className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-black transition-all text-sm font-medium italic"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-[10px] font-black text-black uppercase tracking-widest ml-1">Engleski</label>
                <textarea
                  name="storyPara3En"
                  value={settings.storyPara3En || ''}
                  onChange={handleChange}
                  placeholder="Every couple has a story worth celebrating, and I'm here to transform yours into a cinematic masterpiece."
                  rows="4"
                  className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-black transition-all text-sm font-medium italic"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Image Selection */}
        <div className="space-y-6">
          <h3 className="text-sm font-black text-black uppercase tracking-[0.2em]">Glavna Slika</h3>
          <div className="max-w-md">
            <ImagePickerField 
              label="Odaberi sliku"
              value={settings.storyImage}
              onSelect={(path) => setSettings(prev => ({ ...prev, storyImage: path }))}
            />
          </div>
        </div>

      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={loading}
          className="px-12 py-5 bg-black text-white rounded-[2rem] font-black uppercase tracking-[0.2em] text-[11px] hover:bg-gray-800 transition-all shadow-2xl shadow-black/20"
        >
          {loading ? 'Spašavanje...' : 'Sačuvaj izmjene'}
        </button>
      </div>
    </form>
  </div>
);

const PortfolioSettings = ({ settings, handleChange, loading, handleSettingsSubmit, navigate, setSettings }) => {
  const defaultLabels = [
    { bs: 'Vjenčanja', en: 'Wedding' },
    { bs: 'Filmovi', en: 'Films' },
    { bs: 'Zaručnički', en: 'Engagement' },
    { bs: 'O meni', en: 'About me' }
  ];

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex items-center justify-between mb-8">
        <button 
          onClick={() => navigate('/admin/sections')}
          className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-black hover:text-black transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M15 19l-7-7 7-7" />
          </svg>
          Nazad na listu
        </button>
        <h2 className="text-2xl font-black text-black uppercase tracking-tight">Uređivanje: Portfolio</h2>
      </div>

      <form onSubmit={handleSettingsSubmit} className="space-y-12">
        <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm space-y-10">
          
          {/* Main Title Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pb-10 border-b border-gray-50">
            <div className="space-y-2">
              <label className="block text-[10px] font-black text-black uppercase tracking-widest">Glavni naslov (Bosanski)</label>
              <input
                type="text"
                name="portfolioTitleBs"
                value={settings.portfolioTitleBs || ''}
                onChange={handleChange}
                placeholder="Npr. Portfolio"
                className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-black transition-all text-sm font-medium"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-[10px] font-black text-black uppercase tracking-widest">Glavni naslov (Engleski)</label>
              <input
                type="text"
                name="portfolioTitleEn"
                value={settings.portfolioTitleEn || ''}
                onChange={handleChange}
                placeholder="E.g. Portfolio"
                className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-black transition-all text-sm font-medium"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-[10px] font-black text-black uppercase tracking-widest">Opis iznad (Bosanski)</label>
              <textarea
                name="portfolioSubtitleBs"
                value={settings.portfolioSubtitleBs || ''}
                onChange={handleChange}
                rows="2"
                placeholder="Tekst koji se pojavljuje iznad kategorija..."
                className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-black transition-all text-sm font-medium resize-none"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-[10px] font-black text-black uppercase tracking-widest">Opis iznad (Engleski)</label>
              <textarea
                name="portfolioSubtitleEn"
                value={settings.portfolioSubtitleEn || ''}
                onChange={handleChange}
                rows="2"
                placeholder="Text that appears above categories..."
                className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-black transition-all text-sm font-medium resize-none"
              />
            </div>
          </div>

          {/* Portfolio Items Grid Settings */}
          <div className="space-y-8">
            <div className="flex flex-col gap-1">
              <h3 className="text-sm font-black text-black uppercase tracking-[0.2em]">Kategorije Portfolio-a</h3>
              <p className="text-black text-[10px] font-medium italic">Ovdje možete definisati 4 ključne kategorije koje se vide na početnoj strani.</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[1, 2, 3, 4].map(num => {
                const itemLabel = defaultLabels[num-1];
                return (
                  <div key={num} className="p-8 bg-gray-50/50 rounded-[2.5rem] border border-gray-100 space-y-6 hover:bg-white hover:shadow-xl hover:border-black/5 transition-all duration-500 group">
                    <div className="flex items-center justify-between">
                      <span className="px-4 py-1.5 bg-black text-white text-[9px] font-black uppercase tracking-widest rounded-full">Stavka {num}</span>
                      <div className="w-8 h-8 rounded-full bg-white border border-gray-100 flex items-center justify-center text-gray-300 font-bold text-xs">{num}</div>
                    </div>

                    <div className="space-y-5">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <label className="block text-[9px] font-black text-black uppercase tracking-widest ml-1">Naslov (BS)</label>
                          <input
                            type="text"
                            name={`portfolioItem${num}TitleBs`}
                            value={settings[`portfolioItem${num}TitleBs`] || ''}
                            onChange={handleChange}
                            placeholder={itemLabel.bs}
                            className="w-full px-4 py-3 bg-white border border-gray-100 rounded-2xl focus:ring-2 focus:ring-black transition-all text-sm font-medium"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <label className="block text-[9px] font-black text-black uppercase tracking-widest ml-1">Naslov (EN)</label>
                          <input
                            type="text"
                            name={`portfolioItem${num}TitleEn`}
                            value={settings[`portfolioItem${num}TitleEn`] || ''}
                            onChange={handleChange}
                            placeholder={itemLabel.en}
                            className="w-full px-4 py-3 bg-white border border-gray-100 rounded-2xl focus:ring-2 focus:ring-black transition-all text-sm font-medium"
                          />
                        </div>
                      </div>

                      <div className="space-y-4 pt-4 border-t border-gray-100">
                        <ImagePickerField 
                          label="Naslovna Fotografija"
                          value={settings[`portfolioItem${num}Image`]}
                          onSelect={(path) => {
                            setSettings(prev => ({ ...prev, [`portfolioItem${num}Image`]: path }));
                          }}
                        />

                        {/* Video Toggle */}
                        <div className="flex items-center justify-between bg-white px-4 py-3 rounded-2xl border border-gray-100">
                          <div className="flex flex-col">
                            <span className="text-[9px] font-black text-black uppercase tracking-widest">Video indikator</span>
                            <span className="text-[8px] text-black font-medium">Prikaži 'Play' ikonu preko slike</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const key = `portfolioItem${num}IsVideo`;
                              setSettings(prev => ({ ...prev, [key]: !prev[key] }));
                            }}
                            className={`w-10 h-6 rounded-full transition-colors relative ${settings[`portfolioItem${num}IsVideo`] ? 'bg-black' : 'bg-gray-200'}`}
                          >
                            <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${settings[`portfolioItem${num}IsVideo`] ? 'left-5' : 'left-1'}`} />
                          </button>
                        </div>

                        {/* Optional Link to page */}
                        <div className="pt-4">
                          <label className="block text-[9px] font-black text-black uppercase tracking-widest ml-1">Link (opcionalno)</label>
                          <input
                            type="text"
                            name={`portfolioItem${num}Link`}
                            value={settings[`portfolioItem${num}Link`] || ''}
                            onChange={handleChange}
                            placeholder={num === 2 ? '/films' : ''}
                            className="w-full px-4 py-3 bg-white border border-gray-100 rounded-2xl focus:ring-2 focus:ring-black transition-all text-sm font-medium"
                          />
                          <p className="text-[10px] text-gray-500 mt-1">Ako unesete link, klik na stavku će voditi na tu stranicu (relativni ili apsolutni URL).</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex justify-end p-4">
          <button
            type="submit"
            disabled={loading}
            className="px-12 py-5 bg-black text-white rounded-[2rem] font-black uppercase tracking-[0.2em] text-[11px] hover:bg-gray-800 transition-all shadow-2xl shadow-black/20 disabled:opacity-50 active:scale-95"
          >
            {loading ? 'Spašavanje...' : 'Sačuvaj sve izmjene'}
          </button>
        </div>
      </form>
    </div>
  );
};

const AdminSectionSettings = ({ settings, setSettings, handleSettingsSubmit, loading }) => {
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  if (loading) return <div className="p-8 text-center text-xs font-black uppercase tracking-widest text-black animate-pulse">Učitavanje...</div>;

  const commonProps = { settings, setSettings, handleChange, loading, handleSettingsSubmit, navigate };

  return (
    <Routes>
      <Route index element={<SectionList />} />
      <Route path="hero" element={<HeroSettings {...commonProps} />} />
      <Route path="portfolio" element={<PortfolioSettings {...commonProps} />} />
      <Route path="gallery" element={<GallerySettings {...commonProps} />} />
      <Route path="story" element={<StorySettings {...commonProps} />} />
      <Route path="films" element={<FilmsSettings {...commonProps} />} />
    </Routes>
  );
};

const FilmsSettings = ({ settings, handleChange, loading, handleSettingsSubmit, navigate, setSettings }) => {
  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex items-center justify-between mb-8 overflow-x-auto">
        <button 
          onClick={() => navigate('/admin/sections')}
          className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-black hover:text-black transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M15 19l-7-7 7-7" />
          </svg>
          Nazad na listu
        </button>
        <h2 className="text-2xl font-black text-black uppercase tracking-tight whitespace-nowrap">Uređivanje: Films</h2>
      </div>

      <form onSubmit={handleSettingsSubmit} className="space-y-12">
        <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-2">
              <label className="block text-[10px] font-black text-black uppercase tracking-widest">Naslov sekcije (BS)</label>
              <input type="text" name="filmsTitleBs" value={settings.filmsTitleBs || ''} onChange={handleChange} className="w-full px-4 py-3 bg-gray-50 rounded-2xl" />
            </div>
            <div className="space-y-2">
              <label className="block text-[10px] font-black text-black uppercase tracking-widest">Naslov sekcije (EN)</label>
              <input type="text" name="filmsTitleEn" value={settings.filmsTitleEn || ''} onChange={handleChange} className="w-full px-4 py-3 bg-gray-50 rounded-2xl" />
            </div>

            <div className="md:col-span-2">
              <label className="block text-[10px] font-black text-black uppercase tracking-widest">Omogući Films sekciju</label>
              <div className="mt-3">
                <button type="button" onClick={() => setSettings(prev => ({ ...prev, filmsEnabled: !prev.filmsEnabled }))} className={`w-24 h-8 rounded-full ${settings.filmsEnabled ? 'bg-black' : 'bg-gray-200'}`}>
                  <div className={`w-6 h-6 bg-white rounded-full mt-1 ml-1 transition-all ${settings.filmsEnabled ? 'ml-12' : 'ml-1'}`} />
                </button>
                <span className="ml-3 text-sm text-gray-600">{settings.filmsEnabled ? 'Omogućeno' : 'Onemogućeno'}</span>
              </div>
            </div>

            <div className="md:col-span-2 space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <label className="block text-[10px] font-black text-black uppercase tracking-widest">Opis sekcije (BS)</label>
                  <textarea name="filmsDescriptionBs" value={settings.filmsDescriptionBs || ''} onChange={handleChange} rows={3} className="w-full px-4 py-3 bg-gray-50 rounded-2xl" />
                </div>
                <div className="space-y-2">
                  <label className="block text-[10px] font-black text-black uppercase tracking-widest">Opis sekcije (EN)</label>
                  <textarea name="filmsDescriptionEn" value={settings.filmsDescriptionEn || ''} onChange={handleChange} rows={3} className="w-full px-4 py-3 bg-gray-50 rounded-2xl" />
                </div>
              </div>
              <div className="space-y-2">
                <label className="block text-[10px] font-black text-black uppercase tracking-widest">YouTube kanal URL</label>
                <input type="url" name="youtubeChannelUrl" value={settings.youtubeChannelUrl || ''} onChange={handleChange} placeholder="https://www.youtube.com/@nejirazukic" className="w-full px-4 py-3 bg-gray-50 rounded-2xl" />
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button type="submit" disabled={loading} className="px-12 py-5 bg-black text-white rounded-[2rem] font-black uppercase tracking-[0.2em] text-[11px] hover:bg-gray-800 transition-all">
            {loading ? 'Spašavanje...' : 'Sačuvaj izmjene'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminSectionSettings;
